import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing, svg } from 'lit';
import { ref } from 'lit/directives/ref.js';
import { BUSINESS_FLOWS, BUSINESS_LOGIC_VERSION, BUSINESS_MATCHING_RULES } from './business-logic-data.js';
import type { BusinessLanguage, BusinessNode, BusinessText } from './business-logic-types.js';
import { businessLogicStyles } from './business-logic-styles.js';
import { businessFlowLayout } from './business-logic-layout.js';
import { BUSINESS_AUTH_SUMMARIES, type BusinessAuthFlowId } from './business-logic-auth-summary.js';
export {
  businessNodeBox,
  businessFlowGeometry,
  businessEdgeGeometry,
  businessFlowLayout,
} from './business-logic-layout.js';

const LANGUAGE_KEY = 'zw-business-logic-language';
function initialLanguage(): BusinessLanguage {
  try {
    return localStorage.getItem(LANGUAGE_KEY) === 'en' ? 'en' : 'de';
  } catch {
    return 'de';
  }
}
const text = (value: BusinessText, language: BusinessLanguage) => value[language];
const copy = (language: BusinessLanguage, de: string, en: string) => (language === 'de' ? de : en);
const kindLabel = (node: BusinessNode, language: BusinessLanguage) =>
  ({
    start: copy(language, 'Einstieg', 'Entry'),
    stage: copy(language, 'Schritt', 'Stage'),
    decision: copy(language, 'Entscheidung', 'Decision'),
    outcome: copy(language, 'Ergebnis', 'Outcome'),
  })[node.kind];

export const BusinessLogicPage = stateful(() => {
  const [language, setLanguage] = useState<BusinessLanguage>(initialLanguage());
  const [flowID, setFlowID] = useState('existing');
  const [selectedID, setSelectedID] = useState('');
  const [zoom, setZoom] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(1000);
  const [layout] = useState<{
    viewport: HTMLElement | null;
    observer: ResizeObserver | null;
  }>({ viewport: null, observer: null });
  const flow = BUSINESS_FLOWS.find(item => item.id === flowID) ?? BUSINESS_FLOWS[0]!;
  const selected = flow.nodes.find(node => node.id === selectedID) ?? flow.nodes[0]!;
  const authentication = BUSINESS_AUTH_SUMMARIES[flow.id as BusinessAuthFlowId];
  const diagram = businessFlowLayout(flow);
  const geometry = diagram.edges;
  const { width, height } = diagram;
  const fitScale = Math.min(1, Math.max(0.5, (viewportWidth - 32) / width));
  const scale = zoom || fitScale;

  useEffect(
    () => () => {
      layout.observer?.disconnect();
      layout.viewport = null;
    },
    [],
  );
  const bindViewport = (element?: Element) => {
    const next = element as HTMLElement | undefined;
    if (!next || next === layout.viewport) return;
    layout.observer?.disconnect();
    layout.viewport = next;
    const updateWidth = () => {
      if (layout.viewport === next) setViewportWidth(next.clientWidth);
    };
    // Lit ref callbacks run during commit. functional-lit forbids a state update
    // there; defer measurement until commit completes and ignore detached nodes.
    queueMicrotask(updateWidth);
    if (typeof ResizeObserver !== 'undefined') {
      layout.observer = new ResizeObserver(updateWidth);
      layout.observer.observe(next);
    }
  };
  const chooseFlow = (id: string) => {
    if (!BUSINESS_FLOWS.some(item => item.id === id)) return;
    setFlowID(id);
    setSelectedID('');
    setZoom(0);
    if (layout.viewport) layout.viewport.scrollTo({ top: 0, left: 0 });
  };
  const chooseLanguage = (value: BusinessLanguage) => {
    setLanguage(value);
    try {
      localStorage.setItem(LANGUAGE_KEY, value);
    } catch {
      /* Language remains usable when storage is unavailable. */
    }
  };
  const shiftZoom = (difference: number) =>
    setZoom(Math.max(0.5, Math.min(1.5, Math.round((scale + difference) * 100) / 100)));
  const arrowID = `zw-business-arrow-${flow.id}`;

  return html`
    <section class="zw-business" lang=${language} aria-labelledby="zw-business-title">
      ${businessLogicStyles}
      <header class="zw-business-heading">
        <div>
          <p class="zw-business-eyebrow">LeXa / Navigate</p>
          <h1 id="zw-business-title">Business Logic</h1>
          <p class="zw-business-intro">
            ${copy(language, 'Ein klarer Blick auf jeden Gesprächsweg.', 'A clear view of every caller journey.')}
          </p>
        </div>
        <div
          class="zw-business-language"
          role="group"
          aria-label=${copy(language, 'Anzeigesprache', 'Display language')}
        >
          <button type="button" aria-pressed=${language === 'de'} @click=${() => chooseLanguage('de')}>DE</button>
          <button type="button" aria-pressed=${language === 'en'} @click=${() => chooseLanguage('en')}>EN</button>
        </div>
      </header>

      <div class="zw-business-selector-row">
        <label class="zw-business-flow-label" for="zw-business-flow"
          >${copy(language, 'Gesprächsweg auswählen', 'Choose a caller journey')}</label
        >
        <select
          id="zw-business-flow"
          .value=${flow.id}
          @change=${(event: Event) => chooseFlow((event.target as HTMLSelectElement).value)}
        >
          ${BUSINESS_FLOWS.map(
            item =>
              html`<option value=${item.id} ?selected=${item.id === flow.id}>${text(item.label, language)}</option>`,
          )}
        </select>
        <span class="zw-business-path-count"
          >${BUSINESS_FLOWS.length} ${copy(language, 'Gesprächswege', 'caller journeys')}</span
        >
      </div>

      <section class="zw-business-overview" aria-labelledby="zw-business-flow-title">
        <div class="zw-business-overview-main">
          <h2 id="zw-business-flow-title">${text(flow.title, language)}</h2>
          <p>${text(flow.description, language)}</p>
        </div>
        <dl>
          <div>
            <dt>${copy(language, 'Wann beginnt dieser Weg?', 'When does this journey start?')}</dt>
            <dd>${text(flow.entry, language)}</dd>
          </div>
          <div>
            <dt>${copy(language, 'Wohin führt er?', 'Where does it lead?')}</dt>
            <dd>${text(flow.outcome, language)}</dd>
          </div>
        </dl>
      </section>

      <div class="zw-business-workspace">
        <section class="zw-business-canvas-panel" aria-labelledby="zw-business-map-title">
          <header class="zw-business-canvas-toolbar">
            <div>
              <h3 id="zw-business-map-title">${copy(language, 'Ablauf im Überblick', 'Journey overview')}</h3>
              <p>${copy(language, 'Schritt auswählen, um die Regeln zu sehen.', 'Select a stage to see its rules.')}</p>
            </div>
            <div class="zw-business-zoom" role="group" aria-label=${copy(language, 'Diagrammgröße', 'Diagram size')}>
              <button
                type="button"
                @click=${() => shiftZoom(-0.1)}
                ?disabled=${scale <= 0.5}
                aria-label=${copy(language, 'Verkleinern', 'Zoom out')}
              >
                −
              </button>
              <output aria-live="polite">${Math.round(scale * 100)}%</output>
              <button
                type="button"
                @click=${() => shiftZoom(0.1)}
                ?disabled=${scale >= 1.5}
                aria-label=${copy(language, 'Vergrößern', 'Zoom in')}
              >
                +
              </button>
              <button type="button" class="zw-business-fit" @click=${() => setZoom(0)}>
                ${copy(language, 'Einpassen', 'Fit')}
              </button>
            </div>
          </header>
          <div class="zw-business-legend" aria-label=${copy(language, 'Legende', 'Legend')}>
            <span><i class="start"></i>${copy(language, 'Einstieg', 'Entry')}</span>
            <span><i class="stage"></i>${copy(language, 'Schritt', 'Stage')}</span>
            <span><i class="decision"></i>${copy(language, 'Entscheidung', 'Decision')}</span>
            <span><i class="outcome"></i>${copy(language, 'Ergebnis', 'Outcome')}</span>
          </div>
          <div
            class="zw-business-viewport"
            ${ref(bindViewport)}
            tabindex="0"
            role="region"
            aria-label=${copy(language, 'Scrollbares Ablaufdiagramm', 'Scrollable journey diagram')}
          >
            <div
              class="zw-business-scaled"
              style=${`width:${Math.ceil(width * scale)}px;height:${Math.ceil(height * scale)}px`}
            >
              <div class="zw-business-world" style=${`width:${width}px;height:${height}px;transform:scale(${scale})`}>
                <svg
                  class="zw-business-connectors"
                  width=${width}
                  height=${height}
                  viewBox=${`0 0 ${width} ${height}`}
                  aria-hidden="true"
                >
                  <defs>
                    <marker
                      id=${arrowID}
                      markerWidth="8"
                      markerHeight="8"
                      refX="7"
                      refY="4"
                      orient="auto"
                      markerUnits="userSpaceOnUse"
                    >
                      <path d="M 1 1 L 7 4 L 1 7" fill="none" stroke="context-stroke" stroke-width="1.6" />
                    </marker>
                  </defs>
                  ${flow.edges.map(edge => {
                    const route = geometry.get(edge.id)!;
                    return svg`<path class=${edge.from === selected.id || edge.to === selected.id ? 'selected' : ''} d=${route.path} marker-end=${`url(#${arrowID})`} />`;
                  })}
                  ${flow.edges.map(edge => {
                    const route = geometry.get(edge.id)!;
                    return route.crossings.map(point => {
                      const segment = route.points.slice(1).find((b, index) => {
                        const a = route.points[index]!;
                        return (
                          Math.min(a.x, b.x) <= point.x &&
                          Math.max(a.x, b.x) >= point.x &&
                          Math.min(a.y, b.y) <= point.y &&
                          Math.max(a.y, b.y) >= point.y
                        );
                      })!;
                      const vertical = segment.x === point.x;
                      const bridge = vertical
                        ? `M ${point.x} ${point.y - 7} L ${point.x} ${point.y + 7}`
                        : `M ${point.x - 7} ${point.y} L ${point.x + 7} ${point.y}`;
                      return svg`<path class="bridge-gap" d=${bridge} /><path class=${edge.from === selected.id || edge.to === selected.id ? 'selected bridge' : 'bridge'} d=${bridge} />`;
                    });
                  })}
                </svg>
                ${flow.edges.map(edge => {
                  if (!edge.label) return nothing;
                  const route = geometry.get(edge.id)!;
                  return html`<span
                    class="zw-business-edge-label"
                    style=${`left:${route.label.x}px;top:${route.label.y}px;width:${route.labelBounds.right - route.labelBounds.left}px`}
                    >${text(edge.label, language)}</span
                  >`;
                })}
                ${flow.nodes.map((node, index) => {
                  const box = diagram.nodes.get(node.id)!;
                  return html`<button
                    type="button"
                    class=${`zw-business-node ${node.kind}${node.id === selected.id ? ' selected' : ''}`}
                    style=${`left:${box.left}px;top:${box.top}px;width:${box.right - box.left}px;height:${box.bottom - box.top}px`}
                    aria-pressed=${node.id === selected.id}
                    aria-controls="zw-business-detail"
                    aria-label=${`${kindLabel(node, language)}: ${text(node.title, language)}. ${text(node.summary, language)}`}
                    @click=${() => setSelectedID(node.id)}
                  >
                    <span class="zw-business-node-kind"
                      ><span>${String(index + 1).padStart(2, '0')}</span>${kindLabel(node, language)}</span
                    ><strong>${text(node.title, language)}</strong
                    ><span class="zw-business-node-summary">${text(node.summary, language)}</span>
                  </button>`;
                })}
              </div>
            </div>
          </div>
          <footer class="zw-business-map-footer">
            ${copy(
              language,
              'Die Pfeile zeigen Bedingungen und mögliche Ergebnisse. Für Details einen Schritt auswählen.',
              'Arrows show conditions and possible outcomes. Select a stage for details.',
            )}
          </footer>
        </section>

        <aside id="zw-business-detail" class="zw-business-detail" aria-labelledby="zw-business-detail-title">
          <div class="zw-business-detail-head">
            <p class="zw-business-eyebrow">${copy(language, 'Schritt im Detail', 'Stage details')}</p>
            <label class="zw-business-sr-only" for="zw-business-stage"
              >${copy(language, 'Schritt auswählen', 'Choose a stage')}</label
            >
            <select
              id="zw-business-stage"
              .value=${selected.id}
              @change=${(event: Event) => setSelectedID((event.target as HTMLSelectElement).value)}
            >
              ${flow.nodes.map(
                node =>
                  html`<option value=${node.id} ?selected=${node.id === selected.id}>
                    ${text(node.title, language)}
                  </option>`,
              )}
            </select>
            <span class=${`zw-business-detail-kind ${selected.kind}`}>${kindLabel(selected, language)}</span>
            <h3 id="zw-business-detail-title">${text(selected.title, language)}</h3>
            <p>${text(selected.summary, language)}</p>
          </div>
          <div class="zw-business-detail-body">
            <section class="zw-business-auth-summary" aria-labelledby="zw-business-auth-title">
              <p class="zw-business-auth-eyebrow">
                ${copy(language, 'Identitätskriterien für diesen Gesprächsweg', 'Identity criteria for this journey')}
              </p>
              <h4 id="zw-business-auth-title">${text(authentication.title, language)}</h4>
              <p><strong>${copy(language, 'Grundlage', 'Basis')}</strong> ${text(authentication.basis, language)}</p>
              <p><strong>${copy(language, 'E-Mail', 'Email')}</strong> ${text(authentication.email, language)}</p>
              <p>
                <strong>${copy(language, 'Bei Unklarheit', 'When unresolved')}</strong> ${text(
                  authentication.recovery,
                  language,
                )}
              </p>
            </section>
            ${selected.sections.map(
              section =>
                html`<section>
                  <h4>${text(section.title, language)}</h4>
                  ${section.paragraphs.map(paragraph => html`<p>${text(paragraph, language)}</p>`)}
                </section>`,
            )}
            ${flow.notes.length
              ? html`<section class="zw-business-notes">
                  <h4>${copy(language, 'Für diesen Gesprächsweg', 'For this journey')}</h4>
                  <ul>
                    ${flow.notes.map(note => html`<li>${text(note, language)}</li>`)}
                  </ul>
                </section>`
              : nothing}
            <details class="zw-business-sources">
              <summary>${copy(language, 'Quellen und Vergleichswerte', 'Sources and matching reference')}</summary>
              <p>${BUSINESS_LOGIC_VERSION}</p>
              ${selected.sources?.length
                ? html`<ul>
                    ${selected.sources.map(
                      source => html`<li><span>${source.label}</span><code>${source.file}</code></li>`,
                    )}
                  </ul>`
                : nothing}
              <dl class="zw-business-thresholds">
                <div>
                  <dt>${copy(language, 'Vorname', 'First name')}</dt>
                  <dd>${BUSINESS_MATCHING_RULES.name.toFixed(2)}</dd>
                </div>
                <div>
                  <dt>${copy(language, 'Nachname', 'Surname')}</dt>
                  <dd>${BUSINESS_MATCHING_RULES.surname.toFixed(2)}</dd>
                </div>
                <div>
                  <dt>E-Mail</dt>
                  <dd>${BUSINESS_MATCHING_RULES.email.toFixed(2)}</dd>
                </div>
              </dl>
              <p>
                ${copy(
                  language,
                  'Ähnlichkeitswerte sind keine Identitätswahrscheinlichkeiten. Die Regeln des ausgewählten Wegs entscheiden, ob weitere Nachweise nötig sind.',
                  'Similarity scores are not identity probabilities. The selected journey determines whether further evidence is required.',
                )}
              </p>
            </details>
          </div>
        </aside>
      </div>
      <p class="zw-business-reference-note">
        ${copy(
          language,
          'Lesbare Referenz der geprüften Navigate-Logik. Gespeicherte Projekte können Formulierungen und Abläufe ergänzen. Diese Ansicht verändert keine Anrufe oder Einstellungen.',
          'A readable reference of the reviewed Navigate logic. Saved projects may add wording and steps. This view does not change calls or settings.',
        )}
      </p>
    </section>
  `;
});
